/**
 * Defensive future-daily cascade: enumerates each future occurrence of the
 * selected WOs (`getRouteStopsPreview`) and awaits one daily mutation per
 * date, aggregating outcomes into a `MasterCascadeReport`. Catches edge
 * cases the BE master cascade misses (stops migrated to other routes,
 * on-request rows). Only emitted by the `v2-pipeline` strategy — `v1-bulk`
 * relies on `unassignMaster`/`assignMaster` `futureStops:true` instead.
 */
import groupBy from 'lodash/groupBy'

import { asRouteId, asServiceDate, type RouteId, type ServiceDate, type WorkOrderId } from 'types/entities/shared/ids'
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { Deps, MasterCascadeReport, PipelineStep } from 'types/modules/routing'

export type CascadeFutureDailyMode = 'assign' | 'unassign'

export type CascadeFutureDailyPhase = 'ASSIGN_FUTURE_DAILY' | 'UNASSIGN_FUTURE_DAILY'

export interface CascadeFutureDailyStepArgs {
  readonly mode: CascadeFutureDailyMode
  readonly routeId: RouteId
  readonly currentServiceDate: ServiceDate
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
}

export interface CascadeFutureDailyValue {
  readonly mode: CascadeFutureDailyMode
  readonly report: MasterCascadeReport
}

export type CascadeFutureDailyStepResult<TPhase extends CascadeFutureDailyPhase> = StepResult<TPhase, CascadeFutureDailyValue>

const emptyReport: MasterCascadeReport = { attempted: 0, succeeded: 0, failures: [] }

export const makeCascadeFutureDailyStep = <TPhase extends CascadeFutureDailyPhase>(
  args: CascadeFutureDailyStepArgs & { readonly phase: TPhase },
  deps: Deps
): PipelineStep<TPhase, CascadeFutureDailyStepResult<TPhase>> => {
  const stepId = `cascade-future-daily:${args.mode}:${args.routeId}:${args.currentServiceDate}`
  return {
    id: stepId,
    phase: args.phase,
    label:
      args.mode === 'unassign'
        ? 'Failed to unassign stops from future daily schedules'
        : 'Failed to assign stops to future daily schedules',
    applyOrder: 300,
    run: async ctx => {
      if (args.workOrderIds.length === 0) {
        return ok({
          phase: args.phase,
          stepId,
          value: { mode: args.mode, report: emptyReport },
        })
      }

      if (!deps.getRouteStopsPreview) {
        return err({
          kind: 'PRECONDITION_FAILED',
          reason: 'cascadeFutureDaily requires deps.getRouteStopsPreview',
        })
      }

      let previewStops
      try {
        const previewResponse = await deps
          .getRouteStopsPreview({
            body: {
              stops: args.workOrderIds.map(String),
              serviceDate: args.currentServiceDate,
              updateMasterSequence: true,
            },
          })
          .unwrap()
        previewStops = previewResponse?.stops ?? []
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: args.phase,
          status,
          message,
        })
      }

      const futureStops = previewStops.filter(s => s.serviceDate !== (args.currentServiceDate as string))
      if (futureStops.length === 0) {
        return ok({
          phase: args.phase,
          stepId,
          value: { mode: args.mode, report: emptyReport },
        })
      }

      const byDate = groupBy(futureStops, s => s.serviceDate)
      const failures: MasterCascadeReport['failures'][number][] = []
      let succeeded = 0
      const attempted = Object.keys(byDate).length

      // eslint-disable-next-line no-restricted-syntax -- the fanout is deliberately sequential, one service date at a time
      for (const [date, stops] of Object.entries(byDate)) {
        try {
          if (args.mode === 'unassign') {
            const stopIds = stops.map(s => s.workOrderId)
            // eslint-disable-next-line no-await-in-loop
            await deps
              .unassignDaily({
                routeId: args.routeId,
                data: { stopIds, versionContext: ctx.versionContext },
                skipInvalidate: true,
              })
              .unwrap()
            succeeded += 1
          } else {
            const sequenced = stops.map((s, i) => ({
              stopId: String(s.workOrderId),
              sequence: s.sequence ?? i + 1,
            }))
            // eslint-disable-next-line no-await-in-loop
            await deps
              .assignDaily({
                routeId: args.routeId,
                data: {
                  serviceDate: asServiceDate(date),
                  stops: sequenced,
                  versionContext: ctx.versionContext,
                },
                skipInvalidate: true,
              })
              .unwrap()
            succeeded += 1
          }
        } catch (cause: unknown) {
          const { status, message } = extractBackendError(cause)
          failures.push({
            routeId: asRouteId(args.routeId as string),
            serviceDate: asServiceDate(date),
            error: {
              kind: 'BE_REJECTED',
              phase: args.phase,
              status,
              message,
            },
          })
        }
      }

      const report: MasterCascadeReport = { attempted, succeeded, failures }
      return ok({ phase: args.phase, stepId, value: { mode: args.mode, report } })
    },
  }
}
